import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner, Table } from "reactstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import FullPageLoader from "Components/Common/FullPageLoader";
import SectionCard from "Components/Common/SectionCard";
import CommonForm from "Components/Common/CommonForm";
import LineClearanceTable from "./LineClearanceTable";
import { formatDate, GetUserData, showToastMessage } from "common/service/CommonService";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import { LINE_CLEARANCE_PATHS } from "Routes/paths";
import { useLineClearanceService } from "../api/useLineClearance";
import {
  LineClearanceFormValues,
  LineClearanceRow,
  LineClearanceFieldItem,
} from "../helpers/lineClearance.types";
import {
  qaDeclarationFields,
  getLineClearanceRecordFields,
} from "../helpers/lineClearance.constant";
import { queryClient } from "api/queryClient";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import CommonButton from "Components/Common/CommonButton";

/* =================================================================== */
/* Types & Interfaces                                                  */
/* =================================================================== */

interface AddEditLineClearanceQAProps {
  lineclearanceid?: number | string | null;
  onBack: () => void;
}

const AddEditLineClearanceQA: React.FC<AddEditLineClearanceQAProps> = ({
  lineclearanceid,
  onBack,
}) => {
  /* -----------------------------------------------------------------
   * 1. State & Hooks
   * ----------------------------------------------------------------- */
  const navigate = useNavigate();
  const userData = GetUserData();
  const [checklist, setChecklist] = useState<LineClearanceRow[]>([]);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [pendingFormData, setPendingFormData] = useState<LineClearanceFormValues | null>(null);

  /* -----------------------------------------------------------------
   * 2. API Queries
   * ----------------------------------------------------------------- */
  const { SelectQuery, UpdateMutation } = useLineClearanceService({
    lineclearanceid: lineclearanceid ? Number(lineclearanceid) : null,
  });

  /* -----------------------------------------------------------------
   * 3. Derived Data (useMemo)
   * ----------------------------------------------------------------- */
  const selectedRecord = useMemo(() => {
    const rawData = SelectQuery.data?.data;
    if (Array.isArray(rawData)) return rawData[0];
    return rawData || null;
  }, [SelectQuery.data]);

  const isReviewed = useMemo(
    () => Boolean(selectedRecord?.isreviewed),
    [selectedRecord]
  );

  const summaryFields = useMemo(
    () => getLineClearanceRecordFields(selectedRecord),
    [selectedRecord]
  );

  const pairedSummaryFields: LineClearanceFieldItem[][] = useMemo(() => {
    if (!summaryFields.length) return [];
    const paired: LineClearanceFieldItem[][] = [];
    for (let i = 0; i < summaryFields.length; i += 2) {
      paired.push(summaryFields.slice(i, i + 2));
    }
    return paired;
  }, [summaryFields]);

  /* -----------------------------------------------------------------
   * 4. React Hook Form Setup
   * ----------------------------------------------------------------- */
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LineClearanceFormValues>({
    defaultValues: {
      decisionofb: null,
    },
  });

  /* -----------------------------------------------------------------
   * 5. (useEffect)
   * ----------------------------------------------------------------- */
  useEffect(() => {
    if (selectedRecord?.checklist && Array.isArray(selectedRecord.checklist)) {
      const items: LineClearanceRow[] = selectedRecord.checklist.map(
        (q: any, idx: number) => ({
          id: q.lineclearancechecklistid || idx + 1,
          particulars: q.checklistquestion || "",
          pharmacistInfo: q.performedanswer || "yes",
          qaInfo: q.reviewedanswer || "yes",
          qaRemark: q.remark || "",
        })
      );
      setChecklist(items);
    }

    if (
      selectedRecord?.reviewerdecision !== undefined &&
      selectedRecord?.reviewerdecision !== null
    ) {
      setValue("decisionofb", selectedRecord.reviewerdecision ? "yes" : "no");
    }
  }, [selectedRecord, setValue]);

  /* -----------------------------------------------------------------
   * 6. Event Handlers & API Submissions
   * ----------------------------------------------------------------- */
  const handleQaChange = (index: number, value: "yes" | "no" | "na") => {
    setChecklist((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, qaInfo: value } : item))
    );
  };

  const handleRemarkChange = (index: number, value: string) => {
    setChecklist((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, qaRemark: value } : item))
    );
  };

  const handleUpdate = (
    formData: LineClearanceFormValues | null,
    updatereason?: string
  ) => {
    if (!formData) return;

    const updatedChecklist = isReviewed
      ? checklist.filter((item) => {
          const originalMatch = selectedRecord?.checklist?.find(
            (orig: any, idx: number) =>
              (orig.lineclearancechecklistid &&
                item.id &&
                String(orig.lineclearancechecklistid) === String(item.id)) ||
              idx + 1 === item.id ||
              orig.checklistquestion === item.particulars
          );
          if (!originalMatch) return true;
          return (
            item.qaInfo !== (originalMatch.reviewedanswer || "yes") ||
            (item.qaRemark || "") !== (originalMatch.remark || "")
          );
        })
      : checklist;

    const formattedChecklist = updatedChecklist.map((item) => ({
      lineclearancechecklistid: item.id,
      checklistquestion: item.particulars || "",
      reviewedanswer: item.qaInfo || "yes",
      remark: item.qaRemark || "",
    }));

    const payload = {
      lineclearanceid: Number(lineclearanceid),
      reviewedby: Number(userData?.userid),
      updatedby: Number(userData?.userid),
      reviewerdecision: formData.decisionofb === "yes" ? true : false,
      checklist: formattedChecklist,
      periodnumber: selectedRecord?.period_number,
      ipid: selectedRecord?.ipid,
      ...(updatereason ? { updatereason } : {}),
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["line-clearance-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["line-clearance-select"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage(
          "success",
          res?.message ||
            (isReviewed
              ? "Line Clearance QA verification updated successfully."
              : "Line Clearance QA verification saved successfully.")
        );
        setIsUpdateModalOpen(false);
      },
      onError: (error: any) => {
        showToastMessage("error", error || "Failed to submit QA verification");
      },
    });
  };

  const onSubmitQaStep = (formData: LineClearanceFormValues) => {
    if (isReviewed) {
      setPendingFormData(formData);
      setIsUpdateModalOpen(true);
    } else {
      handleUpdate(formData);
    }
  };

  /* -----------------------------------------------------------------
   * 7. Form Render
   * ----------------------------------------------------------------- */
  if (lineclearanceid && (SelectQuery.isLoading || SelectQuery.isFetching)) {
    return <FullPageLoader message="Loading Line Clearance details..." />;
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmitQaStep)}>
        {/* Section 1: Pharmacist Filled Details (Read-only Summary) */}
        <SectionCard
          index={1}
          icon="ri-file-paper-2-line"
          title="Line Clearance Details (Filled by Pharmacist)"
          subtitle="Review details entered by Pharmacist (A) prior to QA verification."
        >
          <div className="table-responsive">
            <Table bordered size="sm" className="align-middle mb-0" style={{ fontSize: "13px" }}>
              <tbody>
                {pairedSummaryFields.map((row, idx) => (
                  <tr key={idx}>
                    <td className="fw-semibold text-dark align-middle px-3 py-2 bg-light border-end" style={{ width: "20%" }}>
                      {row[0].label}
                    </td>
                    <td className="align-middle px-3 py-2 text-dark border-end" style={{ width: "30%" }}>
                      {row[0].value}
                    </td>
                    {row[1] ? (
                      <>
                        <td className="fw-semibold text-dark align-middle px-3 py-2 bg-light border-end" style={{ width: "20%" }}>
                          {row[1].label}
                        </td>
                        <td className="align-middle px-3 py-2 text-dark" style={{ width: "30%" }}>
                          {row[1].value}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="bg-light border-end" style={{ width: "20%" }}></td>
                        <td style={{ width: "30%" }}></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </SectionCard>

        {/* Section 2: QA Line Clearance Verification Checklist */}
        <SectionCard
          index={2}
          icon="ri-checkbox-multiple-line"
          title="Line Clearance QA Verification Checklist"
          subtitle="Inspect Pharmacist entries and record QA verification and remarks for each item."
        >
          <LineClearanceTable
            checklist={checklist}
            onQaChange={handleQaChange}
            onRemarkChange={handleRemarkChange}
            hideQaColumns={false}
            isPharmacistDisabled={true}
            isQaDisabled={false}
          />
        </SectionCard>

        {/* Section 3: QA Person Declaration */}
        <SectionCard
          index={3}
          icon="ri-shield-check-line"
          title="Line Clearance Verification (QA Person)"
          subtitle="Record final clearance status decision by QA Person."
        >
          <CommonForm
            fields={qaDeclarationFields}
            control={control}
            errors={errors}
            setValue={setValue}
            columns={1}
            labelShow={true}
          />
        </SectionCard>

        {/* Sticky Action Footer */}
        <div className={formStyles.stickyFooter}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Button
              type="button"
              color="dark"
              outline
              onClick={onBack}
            >
              Back
            </Button>
            <div className="d-flex gap-2">
              {isReviewed ?
              (<CommonButton
                module={PERMISSION_MODULES.LINE_CLEARANCE}
                action={PERMISSION_ACTIONS.LINE_CLEARANCE_REVIEW}
                type="submit"
                color="primary"
                outline
                disabled={UpdateMutation.isPending}
              >
                Update
              </CommonButton>) 
              :
              (<CommonButton
                module={PERMISSION_MODULES.LINE_CLEARANCE}
                action={PERMISSION_ACTIONS.LINE_CLEARANCE_REVIEW}
                type="submit"
                color="primary"
                outline
                disabled={UpdateMutation.isPending}
              >
                {UpdateMutation.isPending && (
                  <Spinner size="sm" className="me-2" />
                )}
                Save
              </CommonButton>)}
            </div>
          </div>
        </div>
      </form>

      {/* Reason Modal on Update */}
      {isUpdateModalOpen && (
        <Reason
          isOpen={isUpdateModalOpen}
          toggle={() => setIsUpdateModalOpen(false)}
          click={(reasonPayload) => handleUpdate(pendingFormData, reasonPayload.reason)}
          action="Update Line Clearance QA Record"
          id={lineclearanceid ? Number(lineclearanceid) : null}
          isLoading={UpdateMutation.isPending}
        />
      )}
    </>
  );
};

export default AddEditLineClearanceQA;
